import { Vec3, type CollisionHit, type CollisionWorld } from '@mindcloudxai/shared';
import * as THREE from 'three';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';

// 注入 three-mesh-bvh 到 Three.js 原型（模块级单次执行）
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

const GROUND_RAY_LENGTH = 20;
const GROUND_FALLBACK_Y = -100;
const MAX_SLIDE_ITERATIONS = 3;

export interface MeshCollisionWorldOptions {
  /** 碰撞检测半径（sphere cast），默认 0.3 */
  radius?: number;
  /** 可攀爬台阶高度，默认 0.3 */
  maxStepHeight?: number;
}

/**
 * 基于 GLB mesh + BVH 的碰撞世界（Three.js 实现）。
 *
 * - move: 球体 sweep + slide 碰撞（阻挡墙壁，沿壁滑行）
 * - snapToGround: 向下射线吸附
 * - raycast: 通用射线
 */
export class MeshCollisionWorld implements CollisionWorld {
  private meshes: THREE.Mesh[];
  private raycaster = new THREE.Raycaster();
  private downDir = new THREE.Vector3(0, -1, 0);
  private radius: number;
  private maxStepHeight: number;

  constructor(meshes: THREE.Mesh[], options?: MeshCollisionWorldOptions) {
    this.meshes = meshes;
    this.radius = options?.radius ?? 0.3;
    this.maxStepHeight = options?.maxStepHeight ?? 0.3;
    // 为每个 geometry 构建 BVH 加速结构
    for (const mesh of meshes) {
      if (mesh.geometry && !(mesh.geometry as any).boundsTree) {
        (mesh.geometry as any).computeBoundsTree();
      }
    }
  }

  /**
   * 碰撞约束移动：以 from 为起点沿 (to - from) 方向做球体投射，
   * 碰到墙壁后沿表面滑行（最多 MAX_SLIDE_ITERATIONS 次）。
   * Y 轴由调用方（跳跃/重力）控制，此处只做水平+垂直遮挡。
   */
  move(from: Vec3, to: Vec3): Vec3 {
    let current = new THREE.Vector3(from.x, from.y, from.z);
    let target = new THREE.Vector3(to.x, to.y, to.z);
    let remaining = new THREE.Vector3().subVectors(target, current);

    for (let i = 0; i < MAX_SLIDE_ITERATIONS; i++) {
      const dist = remaining.length();
      if (dist < 0.001) break;

      const dir = remaining.clone().normalize();
      this.raycaster.set(current, dir);
      this.raycaster.far = dist + this.radius;

      const hits = this.raycaster.intersectObjects(this.meshes, false);
      const wallHit = hits.find((h) => h.distance < dist + this.radius && h.face);

      if (!wallHit || !wallHit.face) {
        // 无碰撞，直接到达
        current.copy(target);
        break;
      }

      // 碰撞：推到碰撞面前 radius 距离处
      const safeDistance = Math.max(0, wallHit.distance - this.radius - 0.01);
      current.addScaledVector(dir, safeDistance);

      // 计算沿墙滑行方向（移除法线分量）
      const normal = wallHit.face.normal.clone();
      // 将法线从 mesh local 转 world
      normal.transformDirection(wallHit.object.matrixWorld);

      const penetration = dist - safeDistance;
      const slideVec = remaining.clone().normalize().multiplyScalar(penetration);
      slideVec.addScaledVector(normal, -slideVec.dot(normal));

      target.copy(current).add(slideVec);
      remaining.subVectors(target, current);
    }

    return new Vec3(current.x, current.y, current.z);
  }

  /**
   * 从 position 上方向下发射射线，命中则返回 hit.y + eyeHeight。
   */
  snapToGround(position: Vec3, eyeHeight: number): Vec3 {
    // 从脚顶部偏移一点向下打
    const origin = new THREE.Vector3(
      position.x,
      position.y - eyeHeight + this.maxStepHeight + 0.1,
      position.z,
    );
    this.raycaster.set(origin, this.downDir);
    this.raycaster.far = GROUND_RAY_LENGTH;

    const hits = this.raycaster.intersectObjects(this.meshes, false);
    if (hits.length > 0) {
      return new Vec3(position.x, hits[0].point.y + eyeHeight, position.z);
    }
    // 无命中：保持当前 Y（可能在空中）
    return new Vec3(position.x, Math.max(position.y, GROUND_FALLBACK_Y + eyeHeight), position.z);
  }

  /** 通用射线投射 */
  raycast(origin: Vec3, direction: Vec3, maxDistance = 100): CollisionHit | null {
    const o = new THREE.Vector3(origin.x, origin.y, origin.z);
    const d = new THREE.Vector3(direction.x, direction.y, direction.z).normalize();
    this.raycaster.set(o, d);
    this.raycaster.far = maxDistance;

    const hits = this.raycaster.intersectObjects(this.meshes, false);
    if (hits.length === 0) return null;
    const hit = hits[0];
    const normal = hit.face
      ? new Vec3(hit.face.normal.x, hit.face.normal.y, hit.face.normal.z)
      : Vec3.UP;
    return {
      point: new Vec3(hit.point.x, hit.point.y, hit.point.z),
      normal,
      distance: hit.distance,
    };
  }

  dispose(): void {
    for (const mesh of this.meshes) {
      (mesh.geometry as any)?.disposeBoundsTree?.();
    }
    this.meshes = [];
  }
}
